import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../../../layouts/MainLayout';
import PageHeader from '../../../components/PageHeader';
import { isSessionExpiredError } from '../../../api';
import { MarkdownTable } from '../../../components/MarkdownTable';
import {
  collectMarkdownTableLines,
  isTableRowLine,
  isTableSeparatorLine,
} from '../../../utils/markdownTable';
import {
  buildChatStreamRequest,
  streamAssistantChat,
} from '../assistantsService';
const ASSISTANT_NAME = 'Koby';

const AssistantAvatarMark = () => (
  <span className="mi-assistant-avatar" aria-hidden="true">IA</span>
);

const MiSendIcon = () => (
  <svg className="mi-send-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d="M22 2 11 13" />
    <path d="M22 2 15 22 11 13 2 9 22 2z" />
  </svg>
);

const MiStopIcon = () => (
  <svg className="mi-stop-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
    <path d="M17,19.75H7A2.75,2.75,0,0,1,4.25,17V7A2.75,2.75,0,0,1,7,4.25H17A2.75,2.75,0,0,1,19.75,7V17A2.75,2.75,0,0,1,17,19.75ZM7,5.75A1.25,1.25,0,0,0,5.75,7V17A1.25,1.25,0,0,0,7,18.25H17A1.25,1.25,0,0,0,18.25,17V7A1.25,1.25,0,0,0,17,5.75Z" />
  </svg>
);
const MAX_REASONING_LINES = 6;
const MAX_EVIDENCE_SNIPPETS = 5;

const ROUTE_AGENT_SLUGS = {
  '/deep-researcher-competitor': 'market_intel_global',
  '/competitor-web-researcher': 'market_intel_targeted',
  '/state-laws-regulation-deep-search': 'state_laws_regulation_deep_search',
  '/doi-researcher-deep-search': 'doi_researcher_deep_search',
  '/jobs-agent': 'jobs_agent',
};

const rivalStarterPrompts = [
  {
    title: 'Where They are Doubling Down',
    subtitle: 'Identify which products, segments, or journeys are getting disproportionate visibility and emphasis.',
    prompt: 'Identify which products, segments, or journeys are getting disproportionate visibility and emphasis.',
  },
  {
    title: 'What They Believe Wins the Customer',
    subtitle: 'Extract the core value drivers they highlight (price, guarantees, flexibility, digital ease, etc.).',
    prompt: 'Extract the core value drivers they highlight (price, guarantees, flexibility, digital ease, etc.).',
  },
  {
    title: 'Who They are Actively Trying to Acquire',
    subtitle: 'Infer priority customer segments based on messaging, examples, and tailored journeys.',
    prompt: 'Infer priority customer segments based on messaging, examples, and tailored journeys.',
  },
  {
    title: 'How They are Trying to Convert Demand',
    subtitle: 'Break down their funnel strategy - entry points, CTAs, friction removal, and lead capture mechanics.',
    prompt: 'Break down their funnel strategy - entry points, CTAs, friction removal, and lead capture mechanics.',
  },
  {
    title: 'What Has Changed (and Why It Matters)',
    subtitle: 'Detect shifts in messaging, positioning, or flow - and interpret the strategic intent behind it.',
    prompt: 'Detect shifts in messaging, positioning, or flow - and interpret the strategic intent behind it.',
  },
];

const deepStarterPrompts = [
  {
    title: 'Where They are Investing for Growth',
    subtitle: 'Track signals across filings, partnerships, hiring, and announcements to see where capital and focus are going.',
    prompt: 'Track signals across filings, partnerships, hiring, and announcements to see where capital and focus are going.',
  },
  {
    title: 'What Products & Strategies Are Gaining Momentum',
    subtitle: 'Identify which products are expanding via filings, approvals, distribution push, and market mentions.',
    prompt: 'Identify which products are expanding via filings, approvals, distribution push, and market mentions.',
  },
  {
    title: 'How Their Go-To-Market Is Evolving',
    subtitle: 'Decode shifts in distribution (agents, D2C, partners), geographies, and channel strategy.',
    prompt: 'Decode shifts in distribution (agents, D2C, partners), geographies, and channel strategy.',
  },
  {
    title: 'What Risks or Pressures They are Facing',
    subtitle: 'Surface regulatory actions, compliance issues, earnings commentary, or market constraints impacting them.',
    prompt: 'Surface regulatory actions, compliance issues, earnings commentary, or market constraints impacting them.',
  },
  {
    title: 'What They are Likely to Do Next',
    subtitle: 'Synthesize all signals (filings + messaging + hiring + news) to predict upcoming moves and strategy shifts.',
    prompt: 'Synthesize all signals (filings + messaging + hiring + news) to predict upcoming moves and strategy shifts.',
  },
];

const stateLawsStarterPrompts = [
  {
    title: 'What Regulators Are Publishing Frequently',
    subtitle: 'Identify recurring topics across bulletins, notices, and updates.',
    prompt: 'Identify recurring topics across bulletins, notices, and updates.',
  },
  {
    title: 'What New Guidance Has Been Issued',
    subtitle: 'Extract recent circulars, advisories, and interpretation changes.',
    prompt: 'Extract recent circulars, advisories, and interpretation changes.',
  },
  {
    title: 'Where Enforcement Is Visible - Spot fines',
    subtitle: 'Spot fines, actions, and enforcement announcements.',
    prompt: 'Spot fines, actions, and enforcement announcements.',
  },
  {
    title: 'What Filings or Disclosures Are Being Emphasized',
    subtitle: 'Identify new or updated compliance expectations from public guidance.',
    prompt: 'Identify new or updated compliance expectations from public guidance.',
  },
  {
    title: 'What Signals Future Rule Changes',
    subtitle: 'Infer upcoming regulation from draft rules, requests for comments, or public consultations.',
    prompt: 'Infer upcoming regulation from draft rules, requests for comments, or public consultations.',
  },
];

const doiStarterPrompts = [
  {
    title: 'Where Disclosure Expectations Are Tightening',
    subtitle: 'Find DOI guidance and bulletins changing disclosure obligations.',
    prompt: 'Find DOI guidance and bulletins changing disclosure obligations.',
  },
  {
    title: 'What Licensing & Appointment Rules Apply',
    subtitle: 'Summarize producer, carrier, and appointment requirements from DOI sources.',
    prompt: 'Summarize producer, carrier, and appointment requirements from DOI sources.',
  },
  {
    title: 'What Market Conduct Issues Are Surfacing',
    subtitle: 'Identify recurring market conduct themes, penalties, and compliance signals.',
    prompt: 'Identify recurring market conduct themes, penalties, and compliance signals.',
  },
];

const jobsStarterPrompts = [
  {
    title: 'What Are the Biggest Hiring Signals Right Now',
    subtitle: 'Identify the strongest patterns across new roles, teams, leadership hires, and strategic capabilities.',
    prompt: 'Identify the strongest patterns across new roles, teams, leadership hires, and strategic capabilities.',
  },
  {
    title: 'Which Teams & Capabilities Are They Investing In',
    subtitle: 'See where hiring is accelerating across product, distribution, AI, actuarial, compliance, and other key functions.',
    prompt: 'See where hiring is accelerating across product, distribution, AI, actuarial, compliance, and other key functions.',
  },
  {
    title: 'What Product or Market Expansion Signals Are Emerging',
    subtitle: 'Detect hiring patterns that may point to new products, channels, geographies, or growth initiatives.',
    prompt: 'Detect hiring patterns that may point to new products, channels, geographies, or growth initiatives.',
  },
  {
    title: 'What Leadership Hires Should I Pay Attention To',
    subtitle: 'Surface senior hires and newly created leadership roles that may signal strategic or organizational change.',
    prompt: 'Surface senior hires and newly created leadership roles that may signal strategic or organizational change.',
  },
  {
    title: 'How Has Their Hiring Strategy Changed Recently',
    subtitle: 'Compare recent hiring with prior periods to identify new priorities, rising functions, and areas losing momentum.',
    prompt: 'Compare recent hiring with prior periods to identify new priorities, rising functions, and areas losing momentum.',
  },
];

const hostFromUrl = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url || 'source';
  }
};

const INLINE_MARKDOWN_PATTERNS = [
  { type: 'link', regex: /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/ },
  { type: 'code', regex: /`([^`]+)`/ },
  { type: 'bold', regex: /\*\*([^*]+)\*\*/ },
  { type: 'bold', regex: /__([^_]+)__/ },
  { type: 'italic', regex: /\*([^*]+)\*/ },
  { type: 'italic', regex: /_([^_]+)_/ },
];

const BODY_MARKDOWN_OPTIONS = { emphasis: true, code: true };

const renderInlineMarkdown = (value, keyPrefix = 'md', options = BODY_MARKDOWN_OPTIONS) => {
  if (!value) return null;
  if (typeof value !== 'string') return value;

  const nodes = [];
  let cursor = 0;
  let partIndex = 0;

  while (cursor < value.length) {
    let best = null;

    for (const pattern of INLINE_MARKDOWN_PATTERNS) {
      const slice = value.slice(cursor);
      const match = pattern.regex.exec(slice);
      if (!match) continue;
      const index = cursor + match.index;
      if (!best || index < best.index) {
        best = { pattern, match, index };
      }
    }

    if (!best) {
      nodes.push(value.slice(cursor));
      break;
    }

    if (best.index > cursor) {
      nodes.push(value.slice(cursor, best.index));
    }

    const key = `${keyPrefix}-${partIndex}`;
    partIndex += 1;
    const inner = best.match[1];

    if (best.pattern.type === 'link') {
      nodes.push(
        <a key={key} href={best.match[2]} target="_blank" rel="noopener noreferrer">
          {renderInlineMarkdown(inner, `${key}-label`, options)}
        </a>
      );
    } else if (options.code && best.pattern.type === 'code') {
      nodes.push(<code key={key}>{inner}</code>);
    } else if (options.emphasis && best.pattern.type === 'bold') {
      nodes.push(<strong key={key}>{renderInlineMarkdown(inner, `${key}-strong`, options)}</strong>);
    } else if (options.emphasis && best.pattern.type === 'italic') {
      nodes.push(<em key={key}>{renderInlineMarkdown(inner, `${key}-em`, options)}</em>);
    } else {
      nodes.push(renderInlineMarkdown(inner, `${key}-plain`, options));
    }

    cursor = best.index + best.match[0].length;
  }

  if (!nodes.length) return value;
  if (nodes.length === 1 && typeof nodes[0] === 'string') return nodes[0];
  return nodes;
};

const stripMetaSuffix = (text) => {
  let result = text.trim();
  const patterns = [
    /\s*\(\s*1\s*line\s*\)\s*$/i,
    /\s*\(\s*one\s*line\s*\)\s*$/i,
    /\s*\*\(\s*one\s*line\s*\)\*\s*$/i,
    /\s*\(\s*evidence-backed\s*\)\s*$/i,
    /\s*\*\(\s*evidence-backed\s*\)\*\s*$/i,
    /\s*\(\s*\d+\s*[–-]\s*\d+\s+bullets?[^)]*\)\s*$/i,
  ];
  let changed = true;
  while (changed) {
    changed = false;
    for (const pattern of patterns) {
      const next = result.replace(pattern, '');
      if (next !== result) {
        result = next.trim();
        changed = true;
      }
    }
  }
  return result;
};

const lineLabelText = (trimmed) => trimmed
  .replace(/^\d+\.\s+/, '')
  .replace(/^\*\*([^*]+)\*\*$/, '$1')
  .replace(/^__([^_]+)__$/, '$1')
  .trim();

const isDirectAnswerLine = (trimmed) => /^direct answer$/i.test(lineLabelText(trimmed));

const isSectionHeadingLine = (trimmed) => {
  if (isDirectAnswerLine(trimmed)) return false;
  if (/^#{1,3}\s/.test(trimmed)) return true;
  if (/^\*\*[^*]+\*\*$/.test(trimmed) || /^__[^_]+__$/.test(trimmed)) return true;
  if (/^\d+\.\s+/.test(trimmed) && trimmed.length <= 120) return true;
  return false;
};

const isBulletLine = (trimmed) => trimmed.startsWith('- ') || trimmed.startsWith('* ');
const isNumberedLine = (trimmed) => /^\d+\.\s+/.test(trimmed);
const normalizeAssistantMarkdown = (raw) => {
  const source = String(raw || '').replace(/\r\n/g, '\n').trim();
  if (!source) return '';

  const lines = source.split('\n');
  const normalized = [];
  const sectionPattern = /^(summary|key findings|facts vs\.?\s*inference|why it matters|inference|facts)\s*(?:[:\-–]\s*)?(.*)$/i;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      normalized.push('');
      continue;
    }
    if (/^assistant$/i.test(trimmed)) continue;
    if (/^#{1,6}\s+/.test(trimmed)) {
      normalized.push(trimmed);
      continue;
    }
    if (isTableRowLine(trimmed) || isTableSeparatorLine(trimmed)) {
      normalized.push(line);
      continue;
    }

    const sectionMatch = trimmed.match(sectionPattern);
    if (sectionMatch) {
      const title = sectionMatch[1]
        .replace(/\s+/g, ' ')
        .replace(/\b\w/g, (ch) => ch.toUpperCase());
      const rest = (sectionMatch[2] || '').trim();
      normalized.push(`## ${title}`);
      if (rest) normalized.push(rest);
      normalized.push('');
      continue;
    }

    normalized.push(line);
  }

  return normalized.join('\n').replace(/\n{3,}/g, '\n\n').trim();
};
const parseBoldLabeledHeading = (trimmed) => {
  const strongMatch = trimmed.match(/^\*\*([^*]+)\*\*:\s*(.*)$/);
  if (strongMatch) {
    return { heading: strongMatch[1].trim(), rest: (strongMatch[2] || '').trim() };
  }
  const underscoreMatch = trimmed.match(/^__([^_]+)__:\s*(.*)$/);
  if (underscoreMatch) {
    return { heading: underscoreMatch[1].trim(), rest: (underscoreMatch[2] || '').trim() };
  }
  return null;
};

const renderMarkdownTable = (tableLines, tableKey) => (
  <MarkdownTable
    tableLines={tableLines}
    tableKey={tableKey}
    wrapClassName="mi-md-table-wrap"
    tableClassName="mi-md-table"
    renderCell={(cell, cellKey) => renderInlineMarkdown(cell, cellKey, BODY_MARKDOWN_OPTIONS)}
  />
);

const buildNestedList = (items, keyPrefix) => {
  const root = { children: [] };
  const stack = [{ depth: -1, node: root }];

  items.forEach(({ text: itemText, depth }, index) => {
    while (stack.length > 1 && depth <= stack[stack.length - 1].depth) {
      stack.pop();
    }
    const node = { text: itemText, depth, children: [], key: `${keyPrefix}-${index}` };
    stack[stack.length - 1].node.children.push(node);
    stack.push({ depth, node });
  });

  const renderNodes = (nodeList) => (
    <ul key={`${keyPrefix}-ul-${nodeList[0]?.key || 'root'}`}>
      {nodeList.map((node) => (
        <li key={node.key}>
          {renderInlineMarkdown(node.text)}
          {node.children.length ? renderNodes(node.children) : null}
        </li>
      ))}
    </ul>
  );

  return root.children.length ? renderNodes(root.children) : null;
};

const markdownLite = (text) => {
  if (!text) return null;

  const nodes = [];
  let listItems = [];
  const lines = text.split('\n');

  const flushList = () => {
    if (!listItems.length) return;
    nodes.push(buildNestedList(listItems, `ul-${nodes.length}`));
    listItems = [];
  };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const trimmed = stripMetaSuffix(line.trim());
    if (!trimmed) {
      flushList();
      nodes.push(<div key={`sp-${index}`} className="mi-md-spacer" />);
      continue;
    }
    const tableBlock = collectMarkdownTableLines(lines, index, (row) => stripMetaSuffix(String(row || '').trim()));
    if (tableBlock) {
      flushList();
      nodes.push(renderMarkdownTable(tableBlock.tableLines, `tbl-${index}`));
      index = tableBlock.endIndex - 1;
      continue;
    }
    if (/^(?:---|\*\*\*|___)\s*$/.test(trimmed)) {
      flushList();
      nodes.push(<hr key={`hr-${index}`} className="mi-md-hr" />);
      continue;
    }
    if (trimmed.startsWith('> ')) {
      flushList();
      nodes.push(
        <blockquote key={`bq-${index}`}>
          {renderInlineMarkdown(trimmed.slice(2), `bq-${index}`, BODY_MARKDOWN_OPTIONS)}
        </blockquote>
      );
      continue;
    }
    if (isBulletLine(trimmed)) {
      const leadingSpaces = line.match(/^\s*/)?.[0]?.length || 0;
      const depth = Math.floor(leadingSpaces / 2);
      listItems.push({ text: trimmed.slice(2), depth });
      continue;
    }
    flushList();
    const labeledHeading = parseBoldLabeledHeading(trimmed);
    if (labeledHeading?.heading) {
      nodes.push(
        <h3 key={`lh-${index}`}>
          {renderInlineMarkdown(labeledHeading.heading, `lh-h3-${index}`, BODY_MARKDOWN_OPTIONS)}
        </h3>
      );
      if (labeledHeading.rest) {
        nodes.push(
          <p key={`lp-${index}`}>
            {renderInlineMarkdown(labeledHeading.rest, `lh-p-${index}`, BODY_MARKDOWN_OPTIONS)}
          </p>
        );
      }
      continue;
    }
    if (isNumberedLine(trimmed)) {
      nodes.push(
        <p key={index} className="mi-md-list-item">
          {renderInlineMarkdown(trimmed, `ol-${index}`, BODY_MARKDOWN_OPTIONS)}
        </p>
      );
      continue;
    }
    if (isDirectAnswerLine(trimmed)) {
      nodes.push(<p key={index}>{renderInlineMarkdown(lineLabelText(trimmed), `p-${index}`, BODY_MARKDOWN_OPTIONS)}</p>);
      continue;
    }
    if (trimmed.startsWith('### ')) {
      nodes.push(<h3 key={index}>{renderInlineMarkdown(lineLabelText(trimmed.slice(4)), `h3-${index}`, BODY_MARKDOWN_OPTIONS)}</h3>);
      continue;
    }
    if (trimmed.startsWith('## ')) {
      nodes.push(<h2 key={index}>{renderInlineMarkdown(lineLabelText(trimmed.slice(3)), `h2-${index}`, BODY_MARKDOWN_OPTIONS)}</h2>);
      continue;
    }
    if (trimmed.startsWith('# ')) {
      nodes.push(<h1 key={index}>{renderInlineMarkdown(lineLabelText(trimmed.slice(2)), `h1-${index}`, BODY_MARKDOWN_OPTIONS)}</h1>);
      continue;
    }
    if (isSectionHeadingLine(trimmed)) {
      nodes.push(<h3 key={index}>{renderInlineMarkdown(lineLabelText(trimmed), `h3-${index}`, BODY_MARKDOWN_OPTIONS)}</h3>);
      continue;
    }
    nodes.push(<p key={index}>{renderInlineMarkdown(line, `p-${index}`, BODY_MARKDOWN_OPTIONS)}</p>);
  }

  flushList();
  return nodes;
};

const initialPayloadForRoute = (location) => {
  const statePayload = location.state?.assistantPayload;
  if (statePayload?.agent_slug) return statePayload;
  const agentSlug = ROUTE_AGENT_SLUGS[location.pathname];
  return agentSlug
    ? {
      competitors: [],
      is_custom_url: false,
      question: '',
      previous_response_id: null,
      agent_slug: agentSlug,
    }
    : null;
};

const AssistantResearcher = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const payload = useMemo(() => initialPayloadForRoute(location), [location]);
  const [question, setQuestion] = useState(payload?.question || '');
  const [messages, setMessages] = useState([]);
  const [previousResponseId, setPreviousResponseId] = useState(payload?.previous_response_id || null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [streamAnswerDraft, setStreamAnswerDraft] = useState('');
  const [streamReasoningLines, setStreamReasoningLines] = useState([]);
  const [streamPhase, setStreamPhase] = useState('Initializing');
  const [streamEvidenceSnippets, setStreamEvidenceSnippets] = useState([]);
  const streamSourceUrls = useRef(new Set());
  const abortRef = useRef(null);
  const messagesRef = useRef(null);

  const currentAgentSlug = payload?.agent_slug || ROUTE_AGENT_SLUGS[location.pathname] || '';
  const hasConversationContext = Boolean(payload);

  const contextBadgeLabel = useMemo(() => {
    if (!payload) return 'No assistant context';
    if (payload.scope_type) return payload.jurisdiction_name || payload.scope_key || 'Jurisdiction';
    if (payload.is_custom_url) return payload.user_preferred_urls?.[0] || 'Custom URL';
    if (payload.competitors?.length) return payload.competitors.join(', ');
    return 'Assistant';
  }, [payload]);

  const agentBadgeLabel = useMemo(() => {
    if (currentAgentSlug === 'market_intel_global') return 'Deep Researcher Competitor';
    if (currentAgentSlug === 'market_intel_targeted') return 'Competitor Web Researcher';
    if (currentAgentSlug === 'state_laws_regulation_deep_search') return 'State Laws Regulation Deep Search';
    if (currentAgentSlug === 'doi_researcher_deep_search') return 'DOI Researcher Deep Search';
    if (currentAgentSlug === 'jobs_agent') return 'Jobs Agent';
    return currentAgentSlug;
  }, [currentAgentSlug]);

  const starterPrompts = useMemo(() => {
    if (currentAgentSlug === 'market_intel_global') return deepStarterPrompts;
    if (currentAgentSlug === 'state_laws_regulation_deep_search') return stateLawsStarterPrompts;
    if (currentAgentSlug === 'doi_researcher_deep_search') return doiStarterPrompts;
    if (currentAgentSlug === 'jobs_agent') return jobsStarterPrompts;
    return rivalStarterPrompts;
  }, [currentAgentSlug]);

  const streamPhaseToneClass = useMemo(() => {
    const phase = streamPhase.toLowerCase();
    if (phase.includes('research') || phase.includes('source') || phase.includes('search')) return 'is-research';
    if (phase.includes('reading') || phase.includes('evidence')) return 'is-reading';
    if (phase.includes('analy')) return 'is-analysis';
    if (phase.includes('draft') || phase.includes('final')) return 'is-drafting';
    if (phase.includes('initial')) return 'is-initializing';
    return 'is-processing';
  }, [streamPhase]);

  useEffect(() => {
    messagesRef.current?.scrollTo({ top: messagesRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending, streamAnswerDraft, streamEvidenceSnippets]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const resetStreamState = () => {
    setStreamAnswerDraft('');
    setStreamReasoningLines([]);
    setStreamPhase('Initializing');
    setStreamEvidenceSnippets([]);
    streamSourceUrls.current = new Set();
  };

  const handleProgress = (event) => {
    const message = event.message || '';
    if (message && !message.toLowerCase().includes('calling tool')) {
      const cleanLine = message.replace(/^progress\s*:\s*/i, '').trim();
      if (cleanLine) {
        setStreamReasoningLines((current) => [...current, cleanLine].slice(-MAX_REASONING_LINES));
      }
    }

    const lower = message.toLowerCase();
    if (lower.includes('search')) setStreamPhase('Searching');
    else if (lower.includes('read') || lower.includes('analyz')) setStreamPhase('Analyzing');
    else if (lower.includes('complete')) setStreamPhase('Finalizing');

    if (event.url && !streamSourceUrls.current.has(event.url)) {
      streamSourceUrls.current.add(event.url);
      setStreamEvidenceSnippets((current) => [
        ...current,
        {
          url: event.url,
          host: hostFromUrl(event.url),
          title: event.title,
          snippet: event.snippet || 'Collecting source snippet...',
        },
      ].slice(-MAX_EVIDENCE_SNIPPETS));
    }
  };

  const send = async (nextQuestion = question) => {
    const trimmed = nextQuestion.trim();
    if (!trimmed || sending || !payload) return;

    resetStreamState();
    setError(null);
    setSending(true);
    setQuestion('');
    setMessages((current) => [...current, { role: 'user', content: trimmed }]);
    abortRef.current = new AbortController();

    const { endpoint, body } = buildChatStreamRequest({
      payload,
      currentAgentSlug,
      question: trimmed,
      previousResponseId,
    });

    try {
      let completed = false;
      await streamAssistantChat({
        endpoint,
        body,
        signal: abortRef.current.signal,
        onEvent: async (event) => {
          if (event.type === 'progress') {
            handleProgress(event);
          } else if (event.type === 'delta') {
            setStreamAnswerDraft((current) => current + (event.text || ''));
            setStreamPhase('Writing');
          } else if (event.type === 'done') {
            setMessages((current) => [...current, {
              role: 'assistant',
              content: event.answer || '',
              searchTrace: event.search_trace || [],
            }]);
            setPreviousResponseId(event.response_id || null);
            setSending(false);
            setStreamAnswerDraft('');
            completed = true;
          } else if (event.type === 'error') {
            throw new Error(event.detail || 'Backend interruption reported.');
          }
        },
      });
      if (!completed) {
        setSending(false);
      }
    } catch (err) {
      if (isSessionExpiredError(err)) return;
      if (err.name !== 'AbortError') {
        setError(err.message || 'Communication line broken.');
      }
      setSending(false);
    }
  };

  const stop = () => {
    abortRef.current?.abort();
    setSending(false);
  };

  const sourceTrace = messages.flatMap((message) => message.searchTrace || []);
  const inChatView = hasConversationContext && (messages.length > 0 || sending);

  return (
    <Layout>
      <PageHeader title="Assistants" />
      <div className="mi-page">
        {error && <p className="mi-error">{error}</p>}

        {!hasConversationContext && (
          <div className="mi-empty-context">
            <h2>No assistant context found</h2>
            <p>Choose an assistant configuration first.</p>
            <button type="button" onClick={() => navigate('/assistants')}>Back to Assistants</button>
          </div>
        )}

        {hasConversationContext && messages.length === 0 && !sending && (
          <div className="mi-first-screen">
            <div className="mi-first-shell">
              <h2 className="mi-first-title">What are you working on?</h2>
              <p className="mi-first-subtitle">Ask a complex question. Get citation-backed answers</p>

              <div className="mi-prompt-shell">
                <textarea
                  className="mi-first-textarea"
                  rows={4}
                  value={question}
                  placeholder="Ask your question here"
                  disabled={sending}
                  onChange={(event) => setQuestion(event.target.value)}
                />

                <div className="mi-prompt-toolbar">
                  <div className="mi-toolbar-context">
                    {agentBadgeLabel && <span className="mi-context-badge mi-context-badge-agent">{agentBadgeLabel}</span>}
                    <span className="mi-context-badge mi-context-badge-main mi-context-badge-emphasis">
                      <span className="mi-context-dot" aria-hidden="true" />
                      {contextBadgeLabel}
                    </span>
                  </div>
                  <button type="button" className="mi-send" disabled={!question.trim() || sending} onClick={() => send()}>
                    <MiSendIcon />
                    Ask Koby
                  </button>
                </div>
              </div>

              <div className="mi-first-suggestions">
                {starterPrompts.map((item) => (
                  <button type="button" className="mi-suggestion-item" key={item.title} onClick={() => setQuestion(item.prompt)}>
                    <span className="mi-suggestion-arrow" aria-hidden="true">
                      <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                        <path d="M6 18L18 6" />
                        <path d="M10 6H18V14" />
                      </svg>
                    </span>
                    <span className="mi-suggestion-copy">
                      <span className="mi-suggestion-title">{item.title}</span>
                      <span className="mi-suggestion-subtitle">{item.subtitle}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {inChatView && (
          <div className="mi-chat">
            <div className="mi-chat-body">
              <div className="mi-chat-main">
                <div className="mi-messages" ref={messagesRef}>
                  <div className="mi-messages-layout">
                    {messages.map((message, index) => (
                      message.role === 'user' ? (
                        <div className="mi-bubble user" key={`${message.role}-${index}`}>
                          <pre className="mi-user-text">{message.content}</pre>
                        </div>
                      ) : (
                        <section className="mi-assistant-turn" key={`${message.role}-${index}`}>
                          <div className="mi-assistant-head">
                            <AssistantAvatarMark />
                            <span className="mi-assistant-name">{ASSISTANT_NAME}</span>
                          </div>
                          <div className="mi-bubble assistant">
                            <div className="mi-md">{markdownLite(normalizeAssistantMarkdown(message.content))}</div>
                          </div>
                        </section>
                      )
                    ))}

                    {sending && (
                      <section className="mi-assistant-turn mi-assistant-turn-live" aria-live="polite">
                        <div className="mi-assistant-head mi-assistant-head-live">
                          <AssistantAvatarMark />
                          <span className="mi-assistant-name">{ASSISTANT_NAME}</span>
                        </div>
                        <div className="mi-typing">
                          <div className="mi-live-wrap">
                            <div className="mi-live-loader" aria-hidden="true">
                              <span className="mi-live-loader-dot" />
                              <span className="mi-live-loader-dot" />
                              <span className="mi-live-loader-dot" />
                            </div>
                            <span className="mi-live-title">Research in progress</span>
                            <div className="mi-live-stats">
                              <span className={`mi-live-chip mi-live-chip-phase ${streamPhaseToneClass}`}><strong>Phase:</strong> {streamPhase}</span>
                            </div>

                            {streamReasoningLines.length > 0 && (
                              <div className="mi-live-reasoning">
                                <span className="mi-live-title">Reasoning track</span>
                                <ol className="mi-live-reasoning-list">
                                  {streamReasoningLines.map((line, index) => <li key={`${line}-${index}`}>{line}</li>)}
                                </ol>
                              </div>
                            )}

                            {streamEvidenceSnippets.length > 0 && (
                              <div className="mi-live-evidence">
                                <span className="mi-live-title">Source snippets</span>
                                <ul className="mi-evidence-list">
                                  {streamEvidenceSnippets.map((item) => (
                                    <li className="mi-evidence-item" key={item.url}>
                                      <a className="mi-evidence-host" href={item.url} target="_blank" rel="noopener noreferrer">{item.host}</a>
                                      {item.title && <p className="mi-evidence-title">{item.title}</p>}
                                      <p className="mi-evidence-snippet">{item.snippet}</p>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {streamAnswerDraft && (
                              <div className="mi-live-draft">
                                <span className="mi-live-title">Draft answer</span>
                                <div className="mi-md mi-draft-md">{markdownLite(normalizeAssistantMarkdown(streamAnswerDraft))}</div>
                              </div>
                            )}
                            {!streamEvidenceSnippets.length && !streamAnswerDraft && <p className="mi-live-fallback">Collecting source snippets...</p>}
                          </div>
                        </div>
                      </section>
                    )}
                  </div>
                </div>

                <div className="mi-input-bar">
                  <div className="mi-input-main">
                    <div className="mi-context-badges mi-input-context-badges">
                      {agentBadgeLabel && <span className="mi-context-badge mi-context-badge-agent">{agentBadgeLabel}</span>}
                      <span className="mi-context-badge mi-context-badge-main">{contextBadgeLabel}</span>
                    </div>
                    <textarea
                      className="mi-textarea"
                      rows="2"
                      value={question}
                      placeholder="Ask your question here"
                      disabled={sending}
                      onChange={(event) => setQuestion(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && !event.shiftKey) {
                          event.preventDefault();
                          send();
                        }
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    className={sending ? 'mi-stop' : 'mi-send'}
                    disabled={!sending && !question.trim()}
                    onClick={() => (sending ? stop() : send())}
                  >
                    {sending ? <MiStopIcon /> : <MiSendIcon />}
                    {sending ? 'Stop' : 'Ask Koby'}
                  </button>
                </div>
                <p className="mi-disclaimer">Comply is AI and can make mistakes. Please double-check responses.</p>
              </div>

              <aside className="mi-sources-panel" aria-label="Sources">
                <div className="mi-sources-header">
                  <h3>Sources</h3>
                </div>
                {sourceTrace.length ? (
                  <div className="mi-sources-list">
                    {sourceTrace.map((url, index) => (
                      <a
                        className="mi-source-item"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={url}
                        key={`${url}-${index}`}
                      >
                        <span className="mi-source-index">{index + 1}</span>
                        <span className="mi-source-host">{hostFromUrl(url)}</span>
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="mi-sources-empty">Sources will appear here once research begins.</p>
                )}
              </aside>
            </div>
          </div>
        )}

        {!inChatView && (
          <p className="mi-disclaimer">Comply is AI and can make mistakes. Please double-check responses.</p>
        )}
      </div>
    </Layout>
  );
};

export default AssistantResearcher;
